import './DeleteServiceRequestModal.scss';

import React, {
  memo,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Button,
  Collapse,
  Input,
  message,
  Skeleton,
  Typography,
} from 'antd';
import { BottomSheet } from 'react-spring-bottom-sheet';
import TMIcon from 'tm-icons-library';

import ProfileItem from '../../../../components/ProfileItem/ProfileItem';
import { useTheme } from '../../../../context/ThemeProvider';
import {
  useDeleteTQServiceRequest,
} from '../../../../hooks/mutations/tqConnectMutations/service-requests';
import {
  useTQConnectServiceRequestResponses,
} from '../../../../hooks/queries/tqConnectQueries/service-requests';
import {
  copyToClipboard,
  dynamicItemTitle,
  getSingularOrPlural,
} from '../../../../shared/utils';
import {
  ITQServiceRequestDetails,
} from '../../../../types/tqConnectTypes/service-requests';

const ICON_SIZE = 28;

interface Props {
  serviceRequestId: string;
  serviceRequestDetails: ITQServiceRequestDetails;
  isOpen: boolean;
  closeModal: () => void;
  onDeleteSuccess: () => void;
}

const DeleteServiceRequestModal: React.FC<Props> = ({
  serviceRequestId,
  serviceRequestDetails,
  isOpen,
  closeModal,
  onDeleteSuccess,
}) => {
  const { colors: COLORS } = useTheme();

  const [isExpanded, setIsExpanded] = useState(false);
  const [confirmedName, setConfirmedName] = useState('');

  const serviceRequestTitle = dynamicItemTitle('tq-offering-request', 'lower');

  const { data: serviceRequestResponsesResult, isLoading } =
    useTQConnectServiceRequestResponses(
      serviceRequestId,
      true,
      serviceRequestDetails?.totalResponses,
    );

  const { deleteServiceRequest, isPending: isDeleting } =
    useDeleteTQServiceRequest();

  const { responses, total } = useMemo(() => {
    return {
      responses: serviceRequestResponsesResult?.data ?? [],
      total: serviceRequestResponsesResult?.total ?? 0,
    };
  }, [
    serviceRequestResponsesResult?.data,
    serviceRequestResponsesResult?.total,
  ]);

  const handleDeleteServiceRequest = async () => {
    deleteServiceRequest(serviceRequestId, {
      onSuccess: () => {
        message.success(`${serviceRequestTitle.toTitleCase()} deleted`);
        onDeleteSuccess();
        closeModal();
      },
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setConfirmedName('');
    }
  }, [isOpen]);

  return (
    <BottomSheet
      open={isOpen}
      onDismiss={closeModal}
      className="delete-service-request-bottom-sheet"
      header={
        <Typography.Title
          level={4}
          className="delete-service-request-bottom-sheet__title">
          Delete this {serviceRequestTitle}?
        </Typography.Title>
      }>
      <div className="delete-service-request-bottom-sheet__content">
        <Typography.Paragraph className="delete-service-request-bottom-sheet__text">
          {total > 0
            ? `If you proceed, you will permanently lose ALL the data associated with this ${serviceRequestTitle}. This includes:`
            : `Are you sure you want to delete this ${serviceRequestTitle}?`}
        </Typography.Paragraph>

        {serviceRequestDetails?.visibility === 'public' && (
          <>
            {isLoading ? (
              <Skeleton
                title={{ style: { height: 24 } }}
                active
                paragraph={false}
              />
            ) : (
              <Collapse
                expandIconPosition="right"
                expandIcon={({ isActive }) => (
                  <div className="delete-service-request-bottom-sheet__expand-icon">
                    <TMIcon
                      name="chevron-down"
                      size={ICON_SIZE}
                      color={COLORS.ICON}
                      style={{
                        transform: isActive ? 'rotate(180deg)' : 'rotate(0)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </div>
                )}
                onChange={() => setIsExpanded(!isExpanded)}
                className="delete-service-request-bottom-sheet__collapse">
                <Collapse.Panel
                  key={`delete-service-request-${serviceRequestDetails._id}`}
                  header={
                    <Typography.Title
                      level={5}
                      className="delete-service-request-bottom-sheet__collapse-header">
                      {total}{' '}
                      {getSingularOrPlural(total, 'RESPONSE').toUpperCase()}
                    </Typography.Title>
                  }>
                  {total > 0 ? (
                    responses.map((response) => {
                      const { _id, respondedBy } = response;

                      return (
                        <div
                          key={`delete-service-request-response-item-${_id}`}
                          className="delete-service-request-bottom-sheet__response-item">
                          <ProfileItem
                            avatar={respondedBy?.profilePicUrl}
                            avatarSize={ICON_SIZE}
                            title={respondedBy?.name}
                            titleStyle={{
                              flexGrow: 1,
                              fontSize: 14,
                            }}
                            titleProps={{
                              ellipsis: { rows: 1, tooltip: respondedBy?.name },
                            }}
                          />
                        </div>
                      );
                    })
                  ) : (
                    <div className="delete-service-request-bottom-sheet__no-responses">
                      <Typography.Text>
                        You haven&apos;t received any responses yet!
                      </Typography.Text>
                    </div>
                  )}
                </Collapse.Panel>
              </Collapse>
            )}
          </>
        )}

        <Typography.Paragraph
          className="delete-service-request-bottom-sheet__confirm-label"
          style={{ color: COLORS.TEXT1 }}>
          To confirm, please type{' '}
          <strong
            style={{
              whiteSpace: 'pre-wrap',
            }}>{` "${serviceRequestDetails.title}" `}</strong>
          <TMIcon
            name="copy-outline"
            size={16}
            color={COLORS.TEXT1}
            style={{
              marginInline: 4,
              verticalAlign: 'middle',
              cursor: 'pointer',
            }}
            onClick={() => copyToClipboard(serviceRequestDetails.title)}
          />{' '}
          into the textbox below:
        </Typography.Paragraph>

        <Input
          size="large"
          placeholder={
            serviceRequestDetails.title.length > 40
              ? `${serviceRequestDetails.title.slice(0, 37)}...`
              : serviceRequestDetails.title
          }
          value={confirmedName}
          onChange={(e) => setConfirmedName(e.target.value)}
          className="delete-service-request-bottom-sheet__input"
        />

        <div className="delete-service-request-bottom-sheet__footer">
          <Button
            className="siteBtn grayBtn outlineBtn"
            disabled={isDeleting}
            onClick={closeModal}
            block>
            Cancel
          </Button>
          <Button
            className="siteBtn accentBtnV1 danger"
            disabled={
              isLoading ||
              confirmedName !== serviceRequestDetails.title ||
              isDeleting
            }
            loading={isDeleting}
            onClick={handleDeleteServiceRequest}
            block>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default memo(DeleteServiceRequestModal);
